<template>
  <div v-if="loading" class="p-8 text-center">
    <Loader />
  </div>

  <ValidationObserver v-else slim v-slot="{ handleSubmit, invalid }" ref="observer">
    <form @submit.prevent="handleSubmit(submit)">
      <BaseLayoutSpaced relaxed>
        <ValidationProvider
          v-for="field in formFieldsWithDefaults"
          :key="field.name"
          :name="field.name"
          :vid="field.name"
          :rules="field.validation"
          v-slot="{ errors, failed }"
          slim
        >
          <BaseFormGroup>
            <BaseFormLabel :for="`${_uid}-${field.name}`">
              {{ field.label }} <template v-if="field.validation.required"> * </template>
            </BaseFormLabel>
            <Component
              v-model="data[field.name]"
              :is="field.component"
              :disabled="submitting"
              v-bind="{
                ...field.options.props,
                ...field.options.attrs,
              }"
              :invalid="failed"
              :id="`${_uid}-${field.name}`"
            />
            <BaseFormInlineMessage v-if="failed" danger>
              {{ errors[0] }}
            </BaseFormInlineMessage>
          </BaseFormGroup>
        </ValidationProvider>

        <BaseFormInlineMessage v-if="message" danger>
          {{ message }}
        </BaseFormInlineMessage>

        <div>
          <BaseButton primary type="submit" :disabled="invalid || submitting">
            <span v-if="!submitting">Submit</span>
            <Loader v-else small inverse />
          </BaseButton>
          <BaseButton
            ghost
            type="button"
            :disabled="submitting"
            class="ml-2"
            @click="$emit('cancel')"
          >
            Cancel
          </BaseButton>
        </div>
      </BaseLayoutSpaced>
    </form>
  </ValidationObserver>
</template>

<script>
import { mapActions } from 'vuex';
import veeValidateErrorsMixin from '@/mixins/veeValidateErrorsMixin';
import { HTTP_UNPROCESSABLE_ENTITY, HTTP_OK } from '@/api/http-response-codes';
import { editProductServiceApi, createBasicProductServiceApi } from '@/api/clients/admin';

const defaultProductService = {
  name: '',
  productUuid: null,
  serviceUuid: null,
  optional: false,
  price: 0,
  requirements: null,
};

const defaultField = {
  component: null,
  label: '',
  name: '',
  options: {},
  validation: {},
};

export default {
  name: 'FormProductService',
  mixins: [veeValidateErrorsMixin],
  props: {
    productUuid: {
      type: String,
      required: true,
    },
    productServiceUuid: {
      type: String,
      default: null,
    },
  },
  data: () => ({
    formScope: 'product-service',
    submitting: false,
    loading: false,
    data: {
      ...defaultProductService,
    },
    services: [],
    message: null,
  }),
  computed: {
    formFields() {
      return [
        {
          component: 'BaseFormSelect',
          label: 'Service',
          name: 'serviceUuid',
          options: {
            attrs: {
              name: 'serviceUuid',
            },
            props: {
              options: this.services,
            },
          },
          validation: {
            required: true,
          },
        },
        {
          component: 'BaseFormInput',
          label: 'Name',
          name: 'name',
          options: {
            attrs: {
              type: 'text',
              name: 'name',
              class: 'w-full',
            },
          },
          validation: {
            required: true,
            min: 5,
            max: 255,
          },
        },
        {
          component: 'BaseFormCheckbox',
          name: 'optional',
          options: {
            attrs: {
              name: 'optional',
            },
            props: {
              label: 'Optional',
            },
          },
          validation: {
            //
          },
        },
        this.data.optional
          ? {
              component: 'BaseFormInput',
              label: 'Price',
              name: 'price',
              options: {
                attrs: {
                  type: 'number',
                  name: 'price',
                  step: '0.01',
                },
              },
              validation: {
                required: this.data.optional,
                min: 0,
              },
            }
          : {},
        {
          component: 'BaseFormTextarea',
          label: 'Requirements',
          name: 'requirements',
          options: {
            attrs: {
              name: 'requirements',
            },
          },
          validation: {
            //
          },
        },
      ];
    },
    formFieldsWithDefaults() {
      return this.formFields.map((x) => ({ ...defaultField, ...x }));
    },
  },
  watch: {
    'data.serviceUuid': {
      handler(value) {
        if (!value) return;

        const { label = '', price } = this.services.find((x) => x.value === value) || {};

        if (!this.data.name) {
          this.data.name = label;
        }

        if (this.data.optional) {
          this.data.price = price;
        }
      },
    },
  },
  created() {
    if (this.productServiceUuid) {
      this.edit();
    } else {
      this.create();
    }
  },
  methods: {
    ...mapActions('admin/productServices', ['storeBasicProductService', 'updateProductService']),
    async create() {
      this.loading = true;

      try {
        const { data = {}, status } = await createBasicProductServiceApi(this.productUuid);

        if (status && status === HTTP_OK) {
          this.services = data.services;
          this.data.productUuid = this.productUuid;
        }
      } catch (e) {
        //
      }

      this.loading = false;
    },
    async edit() {
      this.loading = true;

      try {
        const { data = {}, status } = await editProductServiceApi(this.productServiceUuid);

        if (status && status === HTTP_OK) {
          Object.assign(this.data, {}, data.data);
          this.services = data.services;
        }
      } catch (e) {
        //
      }

      this.loading = false;
    },
    async submit() {
      if (this.productServiceUuid) {
        this.update();
      } else {
        this.store();
      }
    },
    async store() {
      this.submitting = true;

      const submitWithErrorHandling = this.handleFormErrors(this.storeBasicProductService);

      await submitWithErrorHandling({ basicProductId: this.productUuid, params: this.data });

      this.submitting = false;
    },
    async update() {
      const { productServiceUuid } = this;

      this.submitting = true;

      const submitWithErrorHandling = this.handleFormErrors(this.updateProductService);

      await submitWithErrorHandling({ productServiceUuid, params: this.data });

      this.submitting = false;
    },
    handleFormErrors(fn) {
      return (...params) =>
        fn(...params)
          .then(() => {
            this.$emit('success');
          })
          .catch(({ status, data } = {}) => {
            // do sth with the error!
            if (status && status === HTTP_UNPROCESSABLE_ENTITY) {
              this.observer = this.$refs.observer;
              this.addServerErrors(data.errors);
            }
          });
    },
  },
};
</script>
